/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { formatMoney as formatMoneySarsa } from 'sarsaparilla';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pull from 'lodash/pull';

import { gnoment } from 'shared-ui/gnoment';
import * as types from '../constants';

// Remember to Create tests if you add to this file

// pass this constant into the readRule() methods as the divisionId
// to find specifically the rules that don't have a divisionId
export const RULE_NOT_SPECIFIC_TO_DIVISION = '';

export const dateDiffs = (entryDate, exitDate) => {
    if (entryDate && exitDate) {
        let start = entryDate;
        let end = exitDate;
        if (!gnoment.isGnoment(end)) {
            end = gnoment(end);
        }
        if (!gnoment.isGnoment(start)) {
            start = gnoment(start);
        }
        // adding a day for counting exit date
        return end.diff(start, 'days') + 1;
    }
    return 0;
};

export const nightsInRange = (entryDate, exitDate) => {
    const days = dateDiffs(entryDate, exitDate);
    const nights = days - 1;
    const dates = [];

    for (let i = 0; i < nights; i += 1) {
        const night = gnoment(entryDate).add(i, 'days');
        dates.push(night);
    }

    return dates;
};

export const readRule = (ruleName, rules = [], divisionId = '') => {
    // check if divisionId, read matching rule.
    if (!isEmpty(rules) && divisionId) {
        const foundRules = rules.filter((rule) => {
            return rule.division_id === divisionId && rule.name === ruleName;
        });

        if (foundRules.length) {
            // hopefully there aren't more than one rule for a permit/division/name
            return foundRules[0];
        }
    }
    // otherwise, filter through all the rules and see if there's a match with RULE_NOT_SPECIFIC_TO_DIVISION
    if (!isEmpty(rules)) {
        const foundRules = rules.filter((rule) => {
            const sameDivision =
                rule.division_id === RULE_NOT_SPECIFIC_TO_DIVISION ||
                rule.division_id === divisionId;
            const sameName = rule.name === ruleName;
            return sameDivision && sameName;
        });
        if (foundRules.length > 1) {
            // grab the one that MATCHES THE DIVISION.
            const matchedRule = foundRules.find(
                (rule) => rule.division_id === divisionId
            );
            if (matchedRule) {
                return matchedRule;
            }
        }
        if (foundRules.length) {
            return foundRules[0];
        }
    }
    // and if a rule still isn't found, return a missing rule object
    return {
        division_id: '',
        end_date: gnoment.invalid(),
        operation: '',
        permit_id: '',
        start_date: gnoment.invalid(),
        target: '',
        type: -1,
        value: '',
        isMissing: true,
    };
};

// convert tells this method whether to convert the time or simply force the timezone
// convert = true:  05:00:00 UTC -> 00:00:00 -5:00
// convert = false: 05:00:00 UTC -> 05:00:00 -5:00
export const parseRuleAsMoment = (rule, timeZone, convert) => {
    if (rule.gnoment) {
        return rule.gnoment.tz(timeZone, !convert);
    }

    if (rule.value) {
        return gnoment.unix(rule.value).tz(timeZone, !convert);
    }

    // return an invalid date
    return gnoment('');
};

export const hasRule = (ruleName, permit = {}, divisionId) => {
    return !readRule(ruleName, permit.rules, divisionId).isMissing;
};

export const readRuleAsMoment = (ruleName, rules, timeZone, divisionId, convert) => {
    const rule = readRule(ruleName, rules, divisionId);
    return parseRuleAsMoment(rule, timeZone, convert);
};

export const readRuleValue = (ruleName, permit = {}, divisionId) => {
    return readRule(ruleName, permit.rules, divisionId).value;
};

export const readRuleAsBoolean = (ruleName, permit = {}, divisionId) => {
    return !!readRuleValue(ruleName, permit, divisionId);
};

export const readRuleOperation = (ruleName, permit = {}, divisionId) => {
    return readRule(ruleName, permit.rules, divisionId).operation;
};

export function getActiveLotteries(permit) {
    return permit.lotteries && filter(permit.lotteries, 'is_active');
}

export const sumItems = (items) => {
    if (isEmpty(items)) {
        return 0;
    }
    let total = 0;
    items.forEach((i) => {
        total += parseInt(i.amount, 10);
    });
    return total;
};

// Requires that all keys and values be unique.
function transformIntoBidirectionalMap(map) {
    const ret = new Map();
    map.forEach((k, v) => {
        ret.set(k, v);
        ret.set(v, k);
    });
    return ret;
}

export const equipmentTypeToStr = new Map();
// water
equipmentTypeToStr.set(1, 'Kayak');
equipmentTypeToStr.set(2, 'Double Kayak');
equipmentTypeToStr.set(3, 'Hard Shell Kayak');
equipmentTypeToStr.set(4, 'Inflatable Kayak');
equipmentTypeToStr.set(5, 'Tandem Kayak');
equipmentTypeToStr.set(6, 'Cataraft');
equipmentTypeToStr.set(7, 'Canoe');
equipmentTypeToStr.set(8, 'Solo Canoe');
equipmentTypeToStr.set(9, 'Drift');
equipmentTypeToStr.set(10, 'Raft');
equipmentTypeToStr.set(11, 'Sweep');
equipmentTypeToStr.set(12, 'SUP');
equipmentTypeToStr.set(13, 'Kicker Motor');
equipmentTypeToStr.set(14, 'Paddle Board');
equipmentTypeToStr.set(15, 'Tube');
equipmentTypeToStr.set(16, 'Dory');
equipmentTypeToStr.set(17, 'Standing Paddle Board');
equipmentTypeToStr.set(18, 'Motorboat');
equipmentTypeToStr.set(19, 'Sand Stakes');

// snow
equipmentTypeToStr.set(1001, 'Snowmobile');

// ground
equipmentTypeToStr.set(2001, 'Vehicle');

equipmentTypeToStr.set(999999, 'Other');
equipmentTypeToStr.set(9999999, 'Unknown');

export const equipmentTypes = transformIntoBidirectionalMap(equipmentTypeToStr);

export const animalTypeToString = new Map();
animalTypeToString.set(1, 'Goat');
animalTypeToString.set(2, 'Horse / Pack Mule');
animalTypeToString.set(3, 'Llama');
animalTypeToString.set(4, 'Dog');
animalTypeToString.set(5, 'Horse/Mule Riding Stock');
animalTypeToString.set(6, 'Dogs or other pets');
animalTypeToString.set(7, 'Burro');
animalTypeToString.set(8, 'Mule');
animalTypeToString.set(9, 'Riding Horse');
animalTypeToString.set(10, 'Stock Horse');

// bi-directional map.  Requires that all keys and values be unique.
export const issuanceTypes = new Map();
issuanceTypes.set(types.PRIVATE, 0);
issuanceTypes.set(types.COMMERCIAL, 1);
issuanceTypes.set(types.ADMIN, 2);

// bi-directional map.  Requires that all keys and values be unique.
let issuanceStats = new Map();
issuanceStats.set(types.PERMIT_PRELIMINARY, 0);
issuanceStats.set(types.PERMIT_IN_PROGRESS, 1);
issuanceStats.set(types.LOTTERY_IN_PROGRESS, 2);
issuanceStats.set(types.LOTTERY_SUBMITTED, 3);
issuanceStats.set(types.LOTTERY_WON, 4);
issuanceStats.set(types.LOTTERY_WON_IN_PROGRESS, 5);
issuanceStats.set(types.LOTTERY_LOST, 6);
issuanceStats.set(types.LOTTERY_DECLINED, 7);
issuanceStats.set(types.LOTTERY_ACCEPTED, 8);
issuanceStats.set(types.LOTTERY_NO_RESPONSE, 9);
issuanceStats.set(types.PERMIT_COMPLETE, 10);
issuanceStats.set(types.PERMIT_ISSUED, 11);
issuanceStats.set(types.CANCELLED_NO_RELEASE, 12);
issuanceStats.set(types.PERMIT_CANCELLED_BY_USER, 13);
issuanceStats.set(types.LOTTERY_CANCELLED, 14);
issuanceStats.set(types.PERMIT_VOIDED, 15);
issuanceStats.set(types.LOTTERY_VOIDED, 16);
issuanceStats.set(types.LOTTERY_REJECTED, 17);

export const issuanceReadable = new Map();
issuanceReadable.set(types.PERMIT_PRELIMINARY, types.PERMIT_PRELIMINARY_READABLE);
issuanceReadable.set(types.PERMIT_IN_PROGRESS, types.PERMIT_IN_PROGRESS_READABLE);
issuanceReadable.set(types.PERMIT_COMPLETE, types.PERMIT_COMPLETE_READABLE);
issuanceReadable.set(types.PERMIT_ISSUED, types.PERMIT_ISSUED_READABLE);
issuanceReadable.set(types.CANCELLED_NO_RELEASE, types.PERMIT_CANCELLED_READABLE);
issuanceReadable.set(types.PERMIT_CANCELLED_BY_USER, types.PERMIT_CANCELLED_READABLE);
issuanceReadable.set(types.PERMIT_VOIDED, types.PERMIT_VOIDED_READABLE);

issuanceStats = transformIntoBidirectionalMap(issuanceStats);
export const issuanceStatuses = issuanceStats;

export const getUnitCardinality = (value, singularNoun, pluralNoun) => {
    return value === 1 ? singularNoun : pluralNoun;
};

// helper methods for generating the correct singular/plural nouns
export const getDayCardinality = (value) => {
    const singularNoun = 'day';
    const pluralNoun = 'days';
    return getUnitCardinality(value, singularNoun, pluralNoun);
};

export const getPersonCardinality = (value) => {
    const singularNoun = 'person';
    const pluralNoun = 'people';
    return getUnitCardinality(value, singularNoun, pluralNoun);
};

export const ruleToErrMap = new Map();
ruleToErrMap.set('Rule violated: SeasonStart', {
    text: () =>
        `We appreciate your eagerness and enthusiasm! However, the season has not started and there are no online reservations available at this time. Please check the "Important Dates" section for more information.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: SeasonEnd', {
    text: () =>
        `We applaud your good taste! However, the season has ended and there are no online reservations available at this time. Please check the "Important Dates" section for more information.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: QuotaPerDay', {
    text: () =>
        `This is a popular activity and, unfortunately, there is not enough quota available for the date(s) you selected. `,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: RegistrationEnd', {
    text: (ruleValue) => `Advanced registration for this permit ended on ${ruleValue}.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: HighUseSeasonStart', {
    text: (ruleValue) =>
        `We appreciate your eagerness and enthusiasm! Unfortunately, the lottery season doesn't begin until ${ruleValue}. Please refer to the "Important Dates" section and try again when the lottery opens.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: HighUseSeasonEnd', {
    text: (ruleValue) =>
        `You made a great selection! However, the lottery season closed on ${ruleValue}. Please refer to the "Important Dates" section and try again when the lottery reopens.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: MaxGroupSize', {
    text: (ruleValue) =>
        `You've got a full house! Please adjust your group so that it doesn't exceed the max group size of ${ruleValue}.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: MaxGroupSizeCommercial', {
    text: (ruleValue) =>
        `You've got a full house! Please adjust your group so that it doesn't exceed the max commercial group size of ${ruleValue}.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: StayLimit', {
    text: (ruleValue) =>
        `This a popular permit activity and, unfortunately, the maximum length of stay is ${ruleValue} ${getDayCardinality(
            ruleValue
        )}. Please adjust your dates.`,
    formatValue: (value) => value / (24 * 60 * 60),
});
ruleToErrMap.set('Rule violated: StayMinimum', {
    text: (ruleValue) =>
        `Can you visit for just a little longer? The minimum length of stay is ${ruleValue} ${getDayCardinality(
            ruleValue
        )}. Please adjust your dates.`,
    formatValue: (value) => value / (24 * 60 * 60),
});
ruleToErrMap.set('Rule violated: MaxReservationsHighUseSeason', {
    text: (ruleValue) =>
        `You have excellent taste! However, you can only register ${ruleValue} ${getUnitCardinality(
            ruleValue,
            'time',
            'times'
        )} this season and have exceeded the max number of applications.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: MaxAccountReservationsOnDay', {
    text: (ruleValue) =>
        `You must really like this location! However, you can only register for ${ruleValue} ${getUnitCardinality(
            ruleValue,
            'permit',
            'permits'
        )} per day.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: MaxAccountNotIssuedReservationsPerSeason', {
    text: (ruleValue) =>
        `You can only have ${ruleValue} ${getUnitCardinality(
            ruleValue,
            'permit',
            'permits'
        )} at a time, and you'll need to use a permit before you can reserve another one.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: HighUseRegistrationStart', {
    text: (ruleValue) =>
        `We appreciate your eagerness and enthusiasm! However, lottery registration doesn't begin until ${ruleValue}. Please check back with us then.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: HighUseRegistrationEnd', {
    text: (ruleValue) =>
        `You made a great selection! However, lottery registration closed on ${ruleValue}. Please refer to the "Important Dates" section and try again when the lottery reopens.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: LotteryDeadlineToConfirm', {
    text: (ruleValue) =>
        `Unfortunately, the deadline to confirm this lottery award has passed. Confirmation for the lottery was required by ${ruleValue}.`,
    formatValue: (value) => gnoment.unix(value).utc().format('MMMM Do, YYYY'),
});
ruleToErrMap.set('Rule violated: IsLeaderTransferable', {
    text: () =>
        `Trust your first instinct and stick with this leader. The leader cannot be changed once a reservation is booked.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: IsCustomerTransferable', {
    text: () =>
        `We understand plans can change, but group members can't be updated once a reservation is booked.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: CancellationDeadline', {
    text: () =>
        `Unfortunately, the cancellation window for this reservation has passed. If you choose to cancel this reservation, you may not receive a refund or the cancellation could result in a penalty based on permit regulations.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: DisallowOvernightStock', {
    text: () => `Horses and other stock animals aren't permitted for overnight trips.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: MaxPartySize', {
    text: (ruleValue) =>
        `You've got quite the entourage! Please adjust the total number of people and stock animals so that it doesn’t' exceed the max of ${ruleValue}..`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: MaxEquipmentPerReservation', {
    text: (ruleValue) =>
        `You might be packing more gear than you'll need. The max amount of equipment allowed is ${ruleValue}.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: MaxOverlappingReservations', {
    text: () => `The selected date overlaps another active reservation.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: ActiveIssuances', {
    text: (ruleValue) =>
        `You may only have ${ruleValue} active reservations at this location.`,
    formatValue: (value) => value,
});
ruleToErrMap.set('Rule violated: MinGroupSize', {
    text: (ruleValue) =>
        `You must have at least ${ruleValue} group members to complete this reservation.`,
    formatValue: (value) => value,
});

// Takes raw text of error string and formats to a friendly error message
export const formatErrorMessage = (errString) => {
    const stringArr = [];
    // If last character is a semicolon, remove it before splitting to avoid creating
    // additional (empty) element in errList
    const errList = errString.replace(/;\s*$/, '').split(';');
    errList.forEach((err) => {
        const nameToValue = err.split('|'); // RuleName|Value, i.e. SeasonStart|4823827
        if (ruleToErrMap.get(nameToValue[0])) {
            const ruleObj = ruleToErrMap.get(nameToValue[0]);
            const ruleValue = nameToValue[1]
                ? ruleObj.formatValue(parseInt(nameToValue[1], 10))
                : '';
            stringArr.push(ruleObj.text(ruleValue));
        } else {
            stringArr.push(err);
        }
    });
    return stringArr.join('\n');
};

export const errorsToStringList = (errors) =>
    flatten(
        errors.map((err) => {
            const errString = typeof err === 'string' ? err : err && err.error;
            return errString ? formatErrorMessage(errString).split('\n') : '';
        })
    ).filter((err) => err !== '');

export const createAvailabilityRangeMap = (startDate, endDate) => {
    const dateMap = {};
    const start = gnoment.isGnoment(startDate)
        ? startDate.utc()
        : gnoment(startDate, 'MM/DD/YYYY').utc();
    const end = gnoment.isGnoment(endDate)
        ? endDate.utc()
        : gnoment(endDate, 'MM/DD/YYYY').utc();
    const dateDifference = end.diff(start, 'days') + 1;
    if (dateDifference > 0) {
        Array.from(new Array(dateDifference), (day, i) => {
            const dt = start.clone();
            dateMap[dt.add(i, 'day').format('MM/DD/YYYY')] = true;
            return null;
        });
    }
    return dateMap;
};

export const feeDisplayModels = new Map();
feeDisplayModels.set(types.ONE_FEE, 0);
feeDisplayModels.set(types.ADD_ON_FEE, 1);

export const feeCalculationModels = new Map();
feeCalculationModels.set(types.NO_USE_FEE, 0);
feeCalculationModels.set(types.FLAT_USE_FEE, 1);
feeCalculationModels.set(types.PER_GROUP_PER_NIGHT_FEE, 2);
feeCalculationModels.set(types.PER_MEMBER_FEE, 3);
feeCalculationModels.set(types.PER_MEMBER_PER_NIGHT_FEE, 4);
feeCalculationModels.set(types.PER_MEMBER_PER_DAY_FEE, 5);
feeCalculationModels.set(types.PER_WATERCRAFT_FEE, 6);
feeCalculationModels.set(types.PER_GROUP_PER_DAY_FEE, 7);
feeCalculationModels.set(types.PER_GROUP_PER_NIGHT_CUSTOM_RANGE_HIGH_USE_ONLY_FEE, 8);

// this pre-calculates fees for display in the order details
export const calculateFees = (division, fees, groupMembers, issuance) => {
    const ret = {};
    ret[types.RESERVATION_FEE] = { amount: 0 };
    ret[types.TOTAL_USE_FEE] = { amount: 0 };
    ret[types.MIN_BALANCE_FEE] = { amount: 0 };

    // transform division fees to fees by SKU
    // rest of the logic is based on calculating fees using amounts associated with the SKU's
    const feesBySKU = {};
    fees.forEach((f) => {
        feesBySKU[f.sku] = f;
    });

    // calculate use fee for each member
    let useFeeSubtotal = 0;
    const countsByMemberType = {};
    const useFeeTotalsByMemberType = {};
    groupMembers.forEach((m) => {
        let fee = null;
        if (m.passholderType === types.ADULT_FEE_TYPE) {
            fee = feesBySKU[types.SKU_ADULT_USE_FEE];
        } else if (m.passholderType === types.CHILD_FEE_TYPE) {
            fee = feesBySKU[types.SKU_CHILD_USE_FEE];
        } else if (m.passholderType === types.INTER_ACCESS_FEE_TYPE) {
            fee = feesBySKU[types.SKU_IAP_USE_FEE];
        } else if (m.passholderType === types.INTER_SENIOR_FEE_TYPE) {
            fee = feesBySKU[types.SKU_ISP_USE_FEE];
        } else if (m.passholderType === types.INTER_YOUTH_ACCESS_FEE_TYPE) {
            fee = feesBySKU[types.SKU_IYAP_USE_FEE];
        } else if (m.passholderType === types.SEASON_CARD_FEE_TYPE) {
            fee = feesBySKU[types.SKU_SPH_USE_FEE];
        } else if (m.passholderType === types.TREATY_CARD_FEE_TYPE) {
            fee = feesBySKU[types.SKU_TCH_USE_FEE];
        }

        let count = countsByMemberType[m.passholderType];
        if (!count) {
            count = 0;
        }
        countsByMemberType[m.passholderType] = count + 1;
        if (fee) {
            const amount = parseInt(fee.amount, 10);
            useFeeSubtotal += amount;
            let total = useFeeTotalsByMemberType[m.passholderType];
            if (!total) {
                total = 0;
            }
            useFeeTotalsByMemberType[m.passholderType] = total + amount;
        }
    });

    // create aggregate counts for different fee types
    if (useFeeTotalsByMemberType[types.ADULT_FEE_TYPE] != null) {
        ret[types.ADULT_USE_FEES] = {
            count: countsByMemberType[types.ADULT_FEE_TYPE],
            amount: useFeeTotalsByMemberType[types.ADULT_FEE_TYPE],
        };
    }
    if (useFeeTotalsByMemberType[types.CHILD_FEE_TYPE] != null) {
        ret[types.CHILD_USE_FEES] = {
            count: countsByMemberType[types.CHILD_FEE_TYPE],
            amount: useFeeTotalsByMemberType[types.CHILD_FEE_TYPE],
        };
    }
    if (useFeeTotalsByMemberType[types.INTER_ACCESS_FEE_TYPE] != null) {
        ret[types.INTER_ACCESS_USE_FEES] = {
            count: countsByMemberType[types.INTER_ACCESS_FEE_TYPE],
            amount: useFeeTotalsByMemberType[types.INTER_ACCESS_FEE_TYPE],
        };
    }
    if (useFeeTotalsByMemberType[types.INTER_SENIOR_FEE_TYPE] != null) {
        ret[types.INTER_SENIOR_USE_FEES] = {
            count: countsByMemberType[types.INTER_SENIOR_FEE_TYPE],
            amount: useFeeTotalsByMemberType[types.INTER_SENIOR_FEE_TYPE],
        };
    }
    if (useFeeTotalsByMemberType[types.INTER_YOUTH_ACCESS_FEE_TYPE] != null) {
        ret[types.INTER_YOUTH_USE_FEES] = {
            count: countsByMemberType[types.INTER_YOUTH_ACCESS_FEE_TYPE],
            amount: useFeeTotalsByMemberType[types.INTER_YOUTH_ACCESS_FEE_TYPE],
        };
    }
    if (useFeeTotalsByMemberType[types.SEASON_CARD_FEE_TYPE] != null) {
        ret[types.SPH_USE_FEES] = {
            count: countsByMemberType[types.SEASON_CARD_FEE_TYPE],
            amount: useFeeTotalsByMemberType[types.SEASON_CARD_FEE_TYPE],
        };
    }
    if (useFeeTotalsByMemberType[types.TREATY_CARD_FEE_TYPE] != null) {
        ret[types.TCH_USE_FEES] = {
            count: countsByMemberType[types.TREATY_CARD_FEE_TYPE],
            amount: useFeeTotalsByMemberType[types.TREATY_CARD_FEE_TYPE],
        };
    }
    ret[types.TOTAL_USE_FEE] = { amount: useFeeSubtotal };

    if (
        !issuance ||
        issuance.bwcaw_include_deposit === null ||
        issuance.bwcaw_include_deposit === true
    ) {
        // calculate minimal balance fee as difference between minRequiredBalance and total use fee
        const minFee = feesBySKU[types.SKU_MIN_BALANCE_FEE];
        if (minFee) {
            const minRequiredBalance = parseInt(minFee.amount, 10);
            if (useFeeSubtotal < minRequiredBalance) {
                ret[types.MIN_BALANCE_FEE] = {
                    amount: minRequiredBalance - useFeeSubtotal,
                };
            }
        }
    }

    const txFee = feesBySKU[types.SKU_TX_FEE];
    if (txFee) {
        const amount = parseInt(txFee.amount, 10);
        ret[types.RESERVATION_FEE] = { amount };
    }

    return ret;
};

export const formatCurrency = (amount) => formatMoneySarsa(amount / 100);

export const getIds = (ids) => {
    const idObj = {};
    const issIdAndPermitId = ids.split(types.RES_ID_DELIMITER);
    if (issIdAndPermitId.length === 2) {
        idObj.issuanceId = issIdAndPermitId[0];
        idObj.permitId = issIdAndPermitId[1];
    }
    return idObj;
};

export const stringifyMoment = (m) =>
    m !== null && gnoment.isGnoment(m) ? gnoment(m).format('MM/DD/YYYY') : '';

export const getValueByStringPath = (obj, path) => {
    return () => get(obj, path);
};

export const checkValidationStyle = (validation, field) =>
    validation[field] ? 'rec-error' : '';

export const validationTypes = new Map();
validationTypes.set(types.REQUIRED_BOOLEAN_FIELD, 0);
validationTypes.set(types.REQUIRED_STRING_FIELD, 1);
validationTypes.set(types.REQUIRED_DATE_FIELD, 2);
validationTypes.set(types.REQUIRED_NUM_FIELD, 3);
validationTypes.set(types.REQUIRED_LIST_ENTRY_FIELD, 4);

export const validationTargets = new Map();
validationTargets.set(types.ENTRY_POINT, 'launchDetail.entryLocationId');
validationTargets.set(types.EXIT_POINT, 'launchDetail.exitLocationId');
validationTargets.set(types.EXIT_DATE, 'launchDetail.exitDate');
validationTargets.set(types.ISSUE_STATION, 'issueStationDetail.selectedStationId');
validationTargets.set(types.GROUP_MEMBERS, 'groupMembers');
validationTargets.set(types.WATERCRAFTS, 'watercrafts');
validationTargets.set(types.LOTTERY_OPEN_DATE, 'lotteryOpenDate');
validationTargets.set(types.LOTTERY_CLOSE_DATE, 'lotteryCloseDate');
validationTargets.set(types.LOTTERY_RUN_DATE, 'lotteryRunDate');
validationTargets.set(types.LOTTERY_DEADLINE_DATE, 'lotteryDeadlineDate');
validationTargets.set(types.LOTTERY_INFO_DISPLAY_DATE, 'lotteryInfoDisplayDate');
validationTargets.set(types.LOTTERY_NOTIFY_DATE, 'lotteryNotifyDate');
validationTargets.set(types.SEASON_START_DATE, 'lotterySeasonStartDate');
validationTargets.set(types.SEASON_END_DATE, 'lotterySeasonEndDate');
validationTargets.set(types.NEED_TO_KNOW_CHECKBOX, 'needToKnowChecked');
validationTargets.set(types.DIVISION_ID, 'division.id');
validationTargets.set(types.SELECTED_DATES, 'selectedDates');

export const getValidationErrorStyle = (validation, field, optionalClassName) => {
    let className = 'rec-error';
    if (optionalClassName) {
        className = optionalClassName;
    }
    if (Array.isArray(field)) {
        return field.some((str) => validation[str]) ? className : '';
    }
    return validation[field] ? className : '';
};

// check division status
export const isDivisionOpen = (status) => {
    return status === types.DIVISION_STATUS.OPEN;
};

// returns the first prop in the divisions map (not guaranteed to be the same if there are multiple divisions)
export const getFirstDivision = (permitDivisions) => {
    for (const prop in permitDivisions) {
        // prevent traversing down the object prototype
        if (!Object.hasOwn(permitDivisions, prop)) {
            continue;
        }
        return permitDivisions[prop];
    }
    // no divisions, error
    return null;
};

export const getIssuanceDivision = (issuance, permit) => {
    // gets the division from the permit, specified by the issuance, or the first division if one not specified
    // or null if nothing.

    const divisions = get(permit, 'divisions', {});
    const divisionId = get(issuance, 'division_id');
    return get(divisions, divisionId, getFirstDivision(divisions));
};

// returns an open division, otherwise returns the first division if there are none open
export const getDivision = (permitDivisions) => {
    for (const id in permitDivisions) {
        if (!Object.hasOwn(permitDivisions, id)) {
            continue;
        }
        const division = permitDivisions[id];
        if (isDivisionOpen(division.status)) {
            return division;
        }
    }

    return getFirstDivision(permitDivisions);
};

// Gets props.currentDate if it exists, otherwise gets the current time.
// Useful for faking what time it is for debugging purposes
export const getCurrentTime = (currentDate) => {
    if (currentDate) {
        return gnoment.isGnoment(currentDate)
            ? currentDate.utc()
            : gnoment(currentDate).utc();
    }

    let currentDateCopy = gnoment().utc();

    // enable time machine date search param only in dev
    if (process.env.NODE_ENV === 'development') {
        const searchParams = new URLSearchParams(window.location.search);
        const dateFromParams = searchParams.get('date');

        currentDateCopy = dateFromParams
            ? gnoment(dateFromParams).utc()
            : gnoment().utc();
    }

    return currentDateCopy;
};

// sales channel types
export const salesChannelTypes = new Map();
salesChannelTypes.set(types.ONLINE, 0);
salesChannelTypes.set(types.FIELD_SALES, 1);
salesChannelTypes.set(types.CALL_CENTER, 2);

// convert sales channel type enum to string literal
export const salesChannelTypeToString = new Map();
salesChannelTypeToString.set(0, 'Online');
salesChannelTypeToString.set(1, 'Field Sales');
salesChannelTypeToString.set(2, 'Call Center');

// check issuance status for bypassing payment
export const canIssuanceStatusBypassPayment = (issuanceStatus) => {
    return (
        issuanceStatus === issuanceStatuses.get(types.LOTTERY_WON) ||
        issuanceStatus === issuanceStatuses.get(types.LOTTERY_WON_IN_PROGRESS) ||
        issuanceStatus === issuanceStatuses.get(types.PERMIT_COMPLETE)
    );
};

// permit types
export const permitTypes = new Map();
permitTypes.set(types.PERMIT_TYPE_RIVER, 0);
permitTypes.set(types.PERMIT_TYPE_HIKING, 1);
permitTypes.set(types.PERMIT_TYPE_CAMPING, 2);
permitTypes.set(types.PERMIT_TYPE_CLIMBING, 3);
permitTypes.set(types.PERMIT_TYPE_WILDLIFE, 4);
permitTypes.set(types.PERMIT_TYPE_ROADENTRY, 5);
permitTypes.set(types.PERMIT_TYPE_EVENT, 6);
permitTypes.set(types.PERMIT_TYPE_BOAT, 7);
permitTypes.set(types.PERMIT_TYPE_SNOWMOBILE, 8);
permitTypes.set(types.PERMIT_TYPE_WILDERNESS, 9);

// convert permit type enum to string literal
export const permitCategoriesToString = new Map();
permitCategoriesToString.set(0, 'River');
permitCategoriesToString.set(1, 'Hiking');
permitCategoriesToString.set(2, 'Camping');
permitCategoriesToString.set(3, 'Climbing');
permitCategoriesToString.set(4, 'Wildlife');
permitCategoriesToString.set(5, 'Road Entry');
permitCategoriesToString.set(6, 'Event');
permitCategoriesToString.set(7, 'Boat');
permitCategoriesToString.set(8, 'Snowmobile');

// helper methods for checking/retrieving rules
export const hasLotteryPreferenceSelectionLimit = (permit, divisionId) => {
    return hasRule(types.LOTTERY_PREFERENCE_SELECTION_LIMIT, permit, divisionId);
};
export const getLotteryPreferenceSelectionLimit = (permit, divisionId) => {
    return parseInt(
        readRuleValue(types.LOTTERY_PREFERENCE_SELECTION_LIMIT, permit, divisionId),
        10
    );
};

export const getLotteryPreferenceType = (permit, divisionId) => {
    return parseInt(
        readRuleValue(types.LOTTERY_PREFERENCES_TYPE, permit, divisionId),
        10
    );
};

export const hasLotteryPreferenceDateDivisionDuration = (permit, divisionId) => {
    return (
        getLotteryPreferenceType(permit, divisionId) ===
        types.LOTTERY_PREFERENCE_TYPES.DATE_DIVISION_DURATION
    );
};

export const hasLotteryPreferenceDateDivisionGroupSize = (permit, divisionId) => {
    return (
        getLotteryPreferenceType(permit, divisionId) ===
            types.LOTTERY_PREFERENCE_TYPES.DATE_DIVISION_GROUP_MIN_MAX ||
        getLotteryPreferenceType(permit, divisionId) ===
            types.LOTTERY_PREFERENCE_TYPES.DATE_DIVISION_GROUP_MAX
    );
};

export const getAllReservedLotteryStatuses = () => {
    const lotteryStatuses = [];
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_SUBMITTED));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_WON));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_WON_IN_PROGRESS));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_LOST));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_DECLINED));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_ACCEPTED));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_NO_RESPONSE));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_CANCELLED));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_VOIDED));
    lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_REJECTED));
    return lotteryStatuses;
};

export const AVAILABILITY_FILTERS = (permit) => {
    return [
        {
            type: types.GROUP_AVAILABILITY_DAILY_KEY_NAME,
            display: types.FILTER_NUMERICAL,
            label: 'Groups',
            labelAlt: 'Groups',
            initialValue: 1,
            minValue: 1,
            showStandalone: false,
            doNotRender: true,
        },
        {
            type: types.GROUP_AVAILABILITY_KEY_NAME,
            display: types.FILTER_NUMERICAL,
            label: 'Groups',
            labelAlt: 'Groups',
            initialValue: 1,
            minValue: 1,
            maxValue: 1,
            showStandalone: false,
            doNotRender: true,
        },
        {
            type: types.MEMBER_AVAILABILITY_KEY_NAME,
            display: types.FILTER_NUMERICAL,
            label: 'People',
            labelAlt: 'Group Size',
            initialValue: null,
            missingInputError: 'Please enter the number of group members',
            minValue: 1,
            maxValue:
                readRuleValue('MaxGroupSize', permit) === ''
                    ? null
                    : parseInt(readRuleValue('MaxGroupSize', permit), 10),
            showStandalone: true,
        },
        {
            type: types.WATERCRAFT_AVAILABILITY_KEY_NAME,
            display: types.FILTER_NUMERICAL,
            label: 'Watercraft',
            labelAlt: 'Watercraft',
            initialValue: null,
            missingInputError: 'Please select watercraft amount',
            minValue: 1,
            maxValue:
                readRuleValue('MaxWatercraftPerReservation', permit) === ''
                    ? null
                    : parseInt(readRuleValue('MaxWatercraftPerReservation', permit), 10),
            showStandalone: true,
        },
    ];
};

export const addFilterValues = (filters) => {
    let item = {
        equipments: [],
        group_info: [],
    };
    for (const key in filters) {
        if (!Object.hasOwn(filters, key)) {
            continue;
        }

        if (key === types.WATERCRAFT_AVAILABILITY_KEY_NAME) {
            const equipmentInfo = {
                equipments: [],
            };
            equipmentInfo.equipments.push({ type: 0, qty: parseInt(filters[key], 10) });
            item = Object.assign(item, equipmentInfo);
        } else if (key === types.MEMBER_AVAILABILITY_KEY_NAME) {
            const memberInfo = {
                group_info: [],
            };
            for (let i = 0; i < filters[key]; i += 1) {
                memberInfo.group_info.push({
                    first_name: '',
                    middle_name: '',
                    last_name: '',
                });
            }
            item = Object.assign(item, memberInfo);
        }
    }
    return item;
};

export function getDisplayOrderNumber(orderId) {
    if (orderId && typeof orderId === 'string') {
        return orderId.substring(orderId.length - 6);
    }
    return orderId;
}

export function calculateEntrancesExits(locations, launchDetail, divisionId, divisions) {
    // divisionId and divisions are optionally arguments

    let entrances = filter(locations, { is_entry: true });
    let exits = filter(locations, { is_exit: true });

    if (divisionId && divisions) {
        const division = divisions[divisionId];

        if (division) {
            if (division.entry_ids) {
                entrances = division.entry_ids.map((entryId) =>
                    locations.find((location) => location.id === entryId)
                );
            }
            if (division.exit_ids) {
                exits = division.exit_ids.map((exitId) =>
                    locations.find((location) => location.id === exitId)
                );
            }
        }
    }

    if (entrances.length === 1 && exits.length === 1) {
        return {
            validEntrances: entrances,
            validExits: exits,
            launchDetail: {
                ...launchDetail,
                entryLocationId: entrances[0].id,
                exitLocationId: exits[0].id,
            },
        };
    }
    if (entrances.length === 1) {
        return {
            validEntrances: entrances,
            validExits: exits,
            launchDetail: {
                ...launchDetail,
                entryLocationId: entrances[0].id,
            },
        };
    }
    if (exits.length === 1) {
        return {
            validEntrances: entrances,
            validExits: exits,
            launchDetail: {
                ...launchDetail,
                exitLocationId: exits[0].id,
            },
        };
    }
    return {
        validEntrances: entrances,
        validExits: exits,
    };
}

export function calculateIssueStations(locations) {
    const issueStations = filter(locations, { is_issue_station: true });

    if (issueStations.length === 1) {
        return {
            validIssueStations: issueStations,
            issueStationDetail: {
                selectedStationId: issueStations[0].id,
            },
        };
    }
    return {
        validIssueStations: issueStations,
    };
}

export const passTypes = {
    None: 'None',
    LocalPass: 'Local Pass',
    InteragencyAccess: 'Interagency Access',
    InteragencySenior: 'Interagency Senior',
};

export const defaultPassOptions = [
    passTypes.None,
    passTypes.LocalPass,
    passTypes.InteragencyAccess,
    passTypes.InteragencySenior,
];

export function loadPassInfo(permit) {
    const passOptions = cloneDeep(defaultPassOptions);

    if (!hasRule('LocalPass', permit)) {
        pull(passOptions, passTypes.LocalPass);
    }

    return passOptions;
}

export const buttonLabels = {
    ProceedToCheckout: 'Proceed to Checkout',
    RemoveFromCart: 'Remove from Cart',
    SaveChanges: 'Save Changes',
    CancelChanges: 'Cancel Changes',
    Cancel: 'Cancel',
};

export function renderList(items, tag) {
    if (!items) {
        return '';
    }
    return items.map((item) => {
        if (tag === 'li') {
            return <li key={item}>{item}</li>;
        }
        if (tag === 'p') {
            return <p key={item}>{item}</p>;
        }
        return '';
    });
}

export function getRecareaName(recarea) {
    let recAreaName = 'Recreation Area';
    if (recarea && recarea[0]) {
        if (recarea[0].name) {
            recAreaName = recarea[0].name;
        }
    }
    return recAreaName;
}

export function getSortedDivisions(divisions, sortOptions) {
    let options = sortOptions;
    if (!options) {
        options = {
            column: ['view_order'],
            isSorted: true,
            isAsc: true,
        };
    }
    const ret = [];
    divisions.forEach((division) => {
        ret[ret.length] = division;
    });
    if (options.isSorted) {
        ret.sort((a, b) => {
            let o1 = '';
            let o2 = '';
            if (Array.isArray(options.column)) {
                options.column.forEach((col) => {
                    o1 += `${a[col]}`.padStart(4);
                    o2 += `${b[col]}`.padStart(4);
                });
            } else {
                o1 = a[options.column];
                o2 = b[options.column];
            }
            if (!options.isAsc) {
                const tmp = o1;
                o1 = o2;
                o2 = tmp;
            }
            if (o1 > o2) {
                return 1;
            }
            if (o1 < o2) {
                return -1;
            }
            return 0;
        });
    }
    return ret;
}

export function getAvailability(
    availabilities,
    availAsOfDate,
    divisionId,
    date,
    quotaType
) {
    const dateKey = availAsOfDate.clone().utc().startOf('day').toISOString();
    const avail = availabilities[dateKey];
    const divAvail = avail?.result?.[divisionId];
    if (!divAvail) return null;

    const formattedDate = `${gnoment.utc(date).format('YYYY-MM-DD')}T00:00:00Z`;
    const dateAvail = divAvail.date_availability;
    if (!quotaType) return dateAvail?.[formattedDate];

    const dateAvailMapped = divAvail.quota_type_maps?.[quotaType];
    return dateAvailMapped?.[formattedDate];
}

export const retrievePhoneNumberFromAccount = (account) => {
    if (!isEmpty(account.cell_phone)) {
        return account.cell_phone;
    }

    return get(account, 'home_phone', '');
};

export function formatMoney(val) {
    return formatMoneySarsa(val);
}

let lastId = 0;
export const getNewId = () => {
    lastId += 1;
    return `${lastId}`;
};

export function getBWCAWWeekStart(date) {
    // calculate beginning of the week as saturday (unless it's week of May 1, then just use may 1)
    let ret = date.clone();
    while (true) {
        if (ret.day() === 6) {
            break;
        }
        ret.add(-1, 'day');
    }
    if (ret.month() === 3 && ret.clone().add(6, 'day').month() === 4) {
        ret = gnoment(date).utc().set('month', 4).set('date', 1);
    }
    return ret;
}

export function getBWCAWWeekEnd(date) {
    // calculate end of the week as friday (unless it's October, then just use Sep 30)
    let ret = date.clone();
    while (true) {
        if (ret.day() === 5) {
            break;
        }
        ret.add(1, 'day');
    }
    if (ret.month() === 9) {
        ret = gnoment(date).utc().set('month', 8).set('date', 30);
    }
    return ret;
}

export function convertIssuanceToGroupMembersObj(groupInfo) {
    const result = [];
    if (groupInfo) {
        for (const member of groupInfo) {
            result.push({
                fName: member.first_name,
                mName: member.middle_name,
                lName: member.last_name,
                startDate: member.start_date,
                endDate: member.end_date,
                passholderType: member.fee_type,
                passholderId: member.passholder_id,
                remark: member.remark,
                isLeader: member.is_leader,
                email: member.email,
                alternateGroupLeaderId: member.alternate_group_leader_id,
            });
        }
    }
    return result;
}

// @todo this function is duplicated in `ui-internal-boundarywaters`
export const getCanadaEntryPointsByPermitType = (permitType) => {
    const entryPoints = [];
    switch (permitType) {
        case types.TYPE_CANADA_PADDLE:
        case types.TYPE_OVERNIGHT_PADDLE: {
            entryPoints.push(
                'Basswood Lake',
                'Basswood River',
                'Birch Lake',
                'Bottle Lake',
                'Bottle River',
                'Carp Lake',
                'Clove Lake',
                'Crooked Lake',
                'Granite River',
                'Iron Lake',
                'Knife Lake',
                'Lac La Croix',
                'Little Vermilion Lake',
                'Loon Lake',
                'Loon River',
                'Moose Lake',
                'Mountain Lake',
                'North Fowl Lake',
                'North Lake',
                'Ottertrack Lake',
                'Pine River',
                'Rose Lake',
                'Rove Lake',
                'Saganaga Lake',
                'South Fowl Lake',
                'South lake',
                'Watap Lake'
            );
            break;
        }
        case types.TYPE_CANADA_MOTOR:
        case types.TYPE_DAYUSE_MOTOR:
        case types.TYPE_OVERNIGHT_MOTOR: {
            entryPoints.push(
                'Basswood Lake',
                'Fall Lake',
                'Lac LaCroix Only',
                'Little Vermillion Lake',
                'Moose Lake',
                'Saganaga Lake',
                'Seagull Lake'
            );
            break;
        }
        case types.TYPE_OVERNIGHT_HIKE:
        default:
    }

    return entryPoints;
};

// cSpell:ignore Cataraft, passholder, recarea, ROADENTRY, bwcaw, IYAP, Lac La Croix, Ottertrack, Saganaga, Watap, DAYUSE_MOTOR
